import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Box } from "@mui/material";
import kai from "../../assets/kai3.png";
import { AskBox } from "./AskBox";
import { ControlledAskBox } from "./sub_comp/ControlledAskBox";
import { RightDock } from "./RightDock";
import { Sidebar } from "./Sidebar";
import { HeaderBar } from "./HeaderBar";
import "./styles.css";
import { useAuth } from "../../AuthContext";
import { useWorkspace } from "../../context/WorkspaceContext";

import { styles } from "./sub_comp/styles";
import { MessageList } from "./sub_comp/MessageList";
import { AgentAnalyzingPanel } from "./sub_comp/AgentAnalyzingPanel";
import { getOrCreateSessionId } from "./sub_comp/session";
import userAvatars from "../../assets/userAvatars";
import {
  AGENT_COLORS,
  detectAgentFromStatus,
  classifyLogEntry,
} from "./utils/tools";
import { AgentReportsRibbon } from "./sub_comp/AgentReportsRibbon";
import {
  RIGHT_DOCK_WIDTH,
  RIGHT_DOCK_TRIGGER_WIDTH,
} from "./sub_comp/layoutConstants";

const API_BASE = "https://webappagentkai.azurewebsites.net/";

// const API_BASE = "http://localhost:5000";
const createTaskTracker = () => ({ sequence: {}, pending: {}, entries: {} });

export default function Home() {
  const { email } = useAuth();
  const { workspace } = useWorkspace();

  // const email = "ashanm01@atkearney.com";

  console.log("User email at Home:", email);

  const [conversationHistory, setConversationHistory] = useState([]);
  const [isResettingMem, setIsResettingMem] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("");
  const [showStatus, setShowStatus] = useState(false);
  const [showRightDock, setShowRightDock] = useState(false);

  // Persist artifacts per assistant response (keyed by runId)
  const [artifactsByRunId, setArtifactsByRunId] = useState({});
  const [toolEventsByRunId, setToolEventsByRunId] = useState({});
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [hasNewAgentReports, setHasNewAgentReports] = useState(false);
  const [runCompletionMap, setRunCompletionMap] = useState({});
  const [visualizationsByRunId, setVisualizationsByRunId] = useState({});
  const clientId = workspace?.clientId || "default";
  const sessionIdState = workspace?.sessionId || "default";

  const [progress, setProgress] = useState([]);
  const [showProgress, setShowProgress] = useState(false);
  const [globalBusy, setGlobalBusy] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);

  const progressPanelRef = useRef(null);
  const askBoxHeroRef = useRef(null);

  const currentRunIdRef = useRef(null);

  const [activeAgents, setActiveAgents] = useState([]);
  const [progressExpanded, setProgressExpanded] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [completedAgents, setCompletedAgents] = useState([]);
  const progressRef = useRef(progress);
  const activeAgentsRef = useRef(activeAgents);
  const planModeRef = useRef(false);
  const planSequenceRef = useRef(0);
  const PLAN_UPDATE_PREFIX = "__plan_update__:";
  const logTaskTrackerRef = useRef(createTaskTracker());
  const eventTaskTrackerRef = useRef(createTaskTracker());
  const pendingDockRunIdRef = useRef(null);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  useEffect(() => {
    activeAgentsRef.current = activeAgents;
  }, [activeAgents]);

  const avatarSrc = userAvatars[email] || userAvatars["default"];

  useEffect(() => {
    if (progressPanelRef.current) {
      progressPanelRef.current.scrollTo({
        top: progressPanelRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [progress]);

  const datasetId = "88187285-0bbb-49fd-b16a-dc0ff83a7ebb";

  const resetTaskTracking = useCallback(() => {
    logTaskTrackerRef.current = createTaskTracker();
    eventTaskTrackerRef.current = createTaskTracker();
  }, []);

  const normalizeAgentLabel = (name) =>
    typeof name === "string" && name.trim().length ? name.trim() : "Agent";

  const getCanonicalAgentKey = (name) => normalizeAgentLabel(name).toLowerCase();

  const ensureTrackerEntry = (tracker, agentName) => {
    const canonical = getCanonicalAgentKey(agentName);
    if (!tracker.entries[canonical]) {
      tracker.entries[canonical] = { runningCount: 0, totalCount: 0 };
    }
    return tracker.entries[canonical];
  };

  const removePendingTaskKey = (tracker, canonicalKey, taskKey) => {
    if (!taskKey) return;
    const queue = tracker.pending[canonicalKey];
    if (!Array.isArray(queue)) return;
    const idx = queue.indexOf(taskKey);
    if (idx >= 0) {
      queue.splice(idx, 1);
    }
  };

  const assignTaskKeyFromTracker = (
    trackerRef,
    agentName,
    phase,
    explicitKey
  ) => {
    if (!trackerRef?.current) return explicitKey || null;
    const tracker = trackerRef.current;
    const label = normalizeAgentLabel(agentName);
    const canonical = getCanonicalAgentKey(agentName);
    const normalizedPhase = phase === "running" ? "running" : "completed";

    const ensureQueue = () => {
      if (!Array.isArray(tracker.pending[canonical])) {
        tracker.pending[canonical] = [];
      }
      return tracker.pending[canonical];
    };

    const nextSequence = () => {
      const next = (tracker.sequence[canonical] || 0) + 1;
      tracker.sequence[canonical] = next;
      return next;
    };

    if (normalizedPhase === "running") {
      const key = explicitKey || `${label}#${nextSequence()}`;
      const queue = ensureQueue();
      queue.push(key);
      const entry = ensureTrackerEntry(tracker, label);
      entry.runningCount = (entry.runningCount || 0) + 1;
      entry.totalCount = (entry.totalCount || 0) + 1;
      return key;
    }

    if (explicitKey) {
      removePendingTaskKey(tracker, canonical, explicitKey);
      const entry = ensureTrackerEntry(tracker, label);
      entry.runningCount = Math.max((entry.runningCount || 1) - 1, 0);
      return explicitKey;
    }

    const queue = ensureQueue();
    if (queue.length > 0) {
      const entry = ensureTrackerEntry(tracker, label);
      entry.runningCount = Math.max((entry.runningCount || 1) - 1, 0);
      return queue.shift();
    }

    const entry = ensureTrackerEntry(tracker, label);
    entry.runningCount = 0;
    return `${label}#${nextSequence()}`;
  };

  const getTrackerStats = (trackerRef, agentName) => {
    if (!agentName || !trackerRef?.current) return null;
    const canonical = getCanonicalAgentKey(agentName);
    return trackerRef.current.entries[canonical] || null;
  };

  const ensureToolEventsLoaded = (runId) => {
    if (!runId) return;
    setToolEventsByRunId((prev) => {
      if (prev[runId]?.length) return prev;
      const snapshot =
        conversationHistory.find(
          (msg) =>
            msg.runId === runId &&
            Array.isArray(msg.toolEventsSnapshot)
        )?.toolEventsSnapshot || [];
      if (!snapshot.length) return prev;
      return { ...prev, [runId]: snapshot };
    });
  };

  const openRunInDock = (runId, { userInitiated = false } = {}) => {
    if (!runId) return;
    ensureToolEventsLoaded(runId);
    setSelectedRunId(runId);
    setShowRightDock(true);
    if (userInitiated) setHasNewAgentReports(false);
  };

  const handleSelectRunFromDock = (runId) => {
    if (!runId || runId === selectedRunId) return;
    ensureToolEventsLoaded(runId);
    setSelectedRunId(runId);
    setHasNewAgentReports(false);
  };

  const runOptions = useMemo(() => {
    const runs = [];
    const seen = new Set();
    conversationHistory.forEach((msg) => {
      if (msg.type !== "assistant") return;
      if (!msg.runId || seen.has(msg.runId)) return;
      const hasArtifacts = Boolean(artifactsByRunId[msg.runId]);
      const hasTools = Boolean(
        (toolEventsByRunId[msg.runId] && toolEventsByRunId[msg.runId].length) ||
          (Array.isArray(msg.toolEventsSnapshot) &&
            msg.toolEventsSnapshot.length)
      );
      if (!hasArtifacts && !hasTools) return;
      seen.add(msg.runId);
      runs.push({
        runId: msg.runId,
        label: `v${runs.length + 1}`,
        hasArtifacts,
        hasTools,
      });
    });
    return runs;
  }, [conversationHistory, artifactsByRunId, toolEventsByRunId]);

  const handleRibbonToggle = () => {
    if (showRightDock) {
      setShowRightDock(false);
      setHasNewAgentReports(false);
      return;
    }
    const targetRunId =
      selectedRunId || runOptions[runOptions.length - 1]?.runId || null;
    if (targetRunId) {
      openRunInDock(targetRunId, { userInitiated: true });
      return;
    }
    setShowRightDock(true);
    setHasNewAgentReports(false);
  };

  const handleArtifacts = (payload) => {
    // Attach artifacts to the current message run
    const runId = currentRunIdRef.current;
    setArtifactsByRunId((prev) => ({
      ...prev,
      [runId]: {
        // keep legacy fields if backend ever sends them
        artifact_image_url:
          payload?.artifact_image_url ??
          prev?.[runId]?.artifact_image_url ??
          null,
        report_url: payload?.report_url ?? prev?.[runId]?.report_url ?? null,
        // NEW: store the artifact plan JSON returned by backend
        artifact_plan:
          payload?.artifact_plan ?? prev?.[runId]?.artifact_plan ?? null,
      },
    }));
  };

  const handleToolEvent = (event) => {
    if (!event) return;
    const runId = currentRunIdRef.current;
    if (!runId) return;
    const agentLabel =
      event.agentName || event.agentSlug || event.toolSlug || "Agent";
    const status =
      typeof event.status === "string" ? event.status.toLowerCase() : "";
    const phase = status === "running" ? "running" : "completed";
    const taskKey = assignTaskKeyFromTracker(
      eventTaskTrackerRef,
      agentLabel,
      phase,
      event.taskKey
    );
    const enrichedEvent = { ...event, taskKey };
    setToolEventsByRunId((prev) => {
      const existing = prev[runId] || [];
      const next = [...existing, enrichedEvent];
      return { ...prev, [runId]: next };
    });
    setHasNewAgentReports(true);
  };

  const fetchVisualizationsForRun = useCallback(
    async (runId) => {
      if (!runId) return;
      setVisualizationsByRunId((prev) => ({
        ...prev,
        [runId]: {
          status: "loading",
          data: prev[runId]?.data || null,
          error: null,
        },
      }));
      try {
        const params = new URLSearchParams({
          limit: "100",
          client_id: clientId,
          session_id: sessionIdState,
          run_id: runId,
        });
        if (email) params.append("email", email);
        const res = await fetch(
          `${API_BASE}/orc/events-artifacts?${params.toString()}`
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(json?.error || `HTTP ${res.status}`);
        }
        const items = Array.isArray(json?.items) ? json.items : [];
        const payload =
          items.length > 0
            ? items[items.length - 1]?.data || items[items.length - 1]
            : null;
        setVisualizationsByRunId((prev) => ({
          ...prev,
          [runId]: {
            status: "ready",
            data: payload,
            error: null,
          },
        }));
      } catch (err) {
        setVisualizationsByRunId((prev) => ({
          ...prev,
          [runId]: {
            status: "error",
            data: null,
            error: err,
          },
        }));
      }
    },
    [API_BASE, clientId, sessionIdState, email]
  );

  const handleUploaded = () => {
    setConversationHistory([
      {
        type: "system",
        message:
          "Dataset uploaded successfully! You can now ask questions about your data.",
        timestamp: new Date(),
      },
    ]);
  };

  const handleAsked = (question) => {
    resetTaskTracking();
    setProgress([]);
    setShowProgress(true);
    setProgressExpanded(false);
    setAnalysisComplete(false);
    setIsAnalyzing(true);
    setCompletedAgents([]);

    const runId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `run_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    currentRunIdRef.current = runId;
    setRunCompletionMap((prev) => ({ ...prev, [runId]: false }));
    setVisualizationsByRunId((prev) => {
      const next = { ...prev };
      delete next[runId];
      return next;
    });

    setConversationHistory((prev) => [
      ...prev,
      { type: "user", message: question, ts: Date.now() },
      {
        type: "assistant",
        message: "",
        pending: true,
        ts: Date.now(),
        runId,
        logsSnapshot: [],
        activeAgentsSnapshot: [],
        toolEventsSnapshot: [],
      },
    ]);

    setIsAnalyzing(true);
    setAnalysisComplete(false);
    setProgressExpanded(false);
  };

  const handleAnswered = (payload) => {
    // Some backends stream artifacts as separate messages
    if (payload?.artifacts) {
      handleArtifacts(payload.artifacts);
      return;
    }

    const text = payload?.answer ?? payload?.text;
    if (!text) return;

    const runId = currentRunIdRef.current;

    // mark all currently active agents as completed once narration arrives
    setCompletedAgents((prev) => {
      const merged = new Set(prev);
      (activeAgentsRef.current || []).forEach((agent) => merged.add(agent));
      return Array.from(merged);
    });

    const completedSet = new Set(activeAgentsRef.current || []);
    setProgress((prev) => {
      const updated = prev.map((entry) => {
        if (entry?.kind === "agent-status") {
          const agentName =
            entry?.meta?.agentName || detectAgentFromStatus(entry.text);
          if (agentName && completedSet.has(agentName)) {
            return {
              ...entry,
              meta: { ...(entry.meta || {}), state: "completed" },
            };
          }
        }
        return entry;
      });
      progressRef.current = updated;
      return updated;
    });

    setConversationHistory((prev) => {
      let found = false;
      const updated = prev.map((m) => {
        if (m.type === "assistant" && m.pending && m.runId === runId) {
          found = true;
          return {
            ...m,
            pending: false,
            message: text,
            logsSnapshot:
              Array.isArray(m.logsSnapshot) && m.logsSnapshot.length
                ? m.logsSnapshot
                : [...(progressRef.current || [])],
            activeAgentsSnapshot:
              Array.isArray(m.activeAgentsSnapshot) &&
              m.activeAgentsSnapshot.length
                ? m.activeAgentsSnapshot
                : [...(activeAgentsRef.current || [])],
            toolEventsSnapshot:
              Array.isArray(m.toolEventsSnapshot) &&
              m.toolEventsSnapshot.length
                ? m.toolEventsSnapshot
                : [...(toolEventsByRunId[runId] || [])],
          };
        }
        return m;
      });

      if (!found) {
        return [
          ...updated,
          {
            type: "assistant",
            pending: false,
            message: text,
            ts: Date.now(),
            runId,
            logsSnapshot: [...progress],
            activeAgentsSnapshot: [...activeAgents],
            toolEventsSnapshot: [...(toolEventsByRunId[runId] || [])],
          },
        ];
      }
      return updated;
    });

    // Collapse the analyzing pane so the user sees the final answer and can reopen if desired.
    setProgressExpanded(false);
    setShowRightDock(false);
  };

  const handleProgressAppend = (text) => {
    if (!text) return;

    const entryId =
      crypto?.randomUUID?.() ?? `${Date.now()}_${Math.random().toString(36)}`;
    let processedText = text;
    const isForcedPlanUpdate =
      typeof processedText === "string" &&
      processedText.startsWith(PLAN_UPDATE_PREFIX);
    if (isForcedPlanUpdate) {
      processedText = processedText.slice(PLAN_UPDATE_PREFIX.length);
    }
    const withinPlan = planModeRef.current;
    const classified = isForcedPlanUpdate
      ? {
          kind: "status-pill",
          text: processedText,
          meta: { source: "plan-update" },
        }
      : classifyLogEntry(processedText, { withinPlan });

    if (classified.kind === "plan-heading") {
      planModeRef.current = true;
      planSequenceRef.current = 0;
    } else if (classified.kind === "plan-paragraph") {
      planSequenceRef.current += 1;
      classified.meta = {
        ...(classified.meta || {}),
        sequence: planSequenceRef.current,
      };
    } else if (withinPlan) {
      planModeRef.current = false;
      planSequenceRef.current = 0;
    }

    const detected = detectAgentFromStatus(processedText);
    const agentLabel =
      detected || classified.meta?.agentName || "Agent";
    let trackerStats = null;
    if (classified.kind === "agent-status" && agentLabel) {
      const state = classified.meta?.state;
      const phase =
        state === "running"
          ? "running"
          : state === "completed" || state === "error"
          ? "completed"
          : null;
      if (phase) {
        const assignedKey = assignTaskKeyFromTracker(
          logTaskTrackerRef,
          agentLabel,
          phase,
          classified.meta?.taskKey
        );
        trackerStats = getTrackerStats(logTaskTrackerRef, agentLabel);
        classified.meta = {
          ...(classified.meta || {}),
          agentName: agentLabel,
          taskKey: assignedKey,
          runningCount: trackerStats?.runningCount || 0,
          totalCount:
            trackerStats?.totalCount || trackerStats?.runningCount || 0,
        };
      }
    }
    if (detected) {
      const lower = text.toLowerCase();
      if (lower.includes("running_now_") || lower.includes("starting_")) {
        setActiveAgents([detected]);
      }
      if (classified.kind === "agent-status") {
        setCompletedAgents((prev) =>
          prev.includes(detected) ? prev : [...prev, detected]
        );
      }
    }

    if (classified.kind === "status-pill" && classified.meta?.signalsCompletion) {
      setCompletedAgents((prev) => {
        const merged = new Set(prev);
        activeAgentsRef.current.forEach((agent) => merged.add(agent));
        return Array.from(merged);
      });
    }

    const canonicalAgentKey = agentLabel
      ? getCanonicalAgentKey(agentLabel)
      : null;
    const mergeCountsIntoEntry = (entry) => {
      const stats = trackerStats || getTrackerStats(logTaskTrackerRef, agentLabel);
      if (!stats) return entry;
      return {
        ...entry,
        meta: {
          ...(entry.meta || {}),
          runningCount: stats.runningCount || 0,
          totalCount: stats.totalCount || stats.runningCount || 0,
        },
      };
    };

    if (
      classified.kind === "agent-status" &&
      classified.meta?.state === "running" &&
      canonicalAgentKey
    ) {
      setProgress((prev) => {
        const next = prev.map((entry) => ({ ...entry }));
        const idx = next.findIndex((entry) => {
          if (entry.kind !== "agent-status") return false;
          const entryName =
            entry.meta?.agentName || detectAgentFromStatus(entry.text);
          if (!entryName) return false;
          return getCanonicalAgentKey(entryName) === canonicalAgentKey;
        });
        if (idx >= 0) {
          next[idx] = mergeCountsIntoEntry({
            ...next[idx],
            meta: {
              ...(next[idx].meta || {}),
              ...(classified.meta || {}),
              state: "running",
            },
          });
          progressRef.current = next;
          return next;
        }
        const appended = [
          ...prev,
          mergeCountsIntoEntry({ id: entryId, text, ...classified }),
        ];
        progressRef.current = appended;
        return appended;
      });
      setShowProgress(true);
      return;
    }

    if (
      classified.kind === "agent-status" &&
      classified.meta?.state === "completed" &&
      detected
    ) {
      setProgress((prev) => {
        const next = prev.map((entry) => ({ ...entry }));
        const idx = next.findIndex((entry) => {
          if (entry.kind !== "agent-status") return false;
          const entryName =
            entry.meta?.agentName || detectAgentFromStatus(entry.text);
          if (!entryName) return false;
          return (
            getCanonicalAgentKey(entryName) === canonicalAgentKey &&
            entry.meta?.state !== "completed"
          );
        });
        const stats = getTrackerStats(logTaskTrackerRef, agentLabel);
        const runningCount = stats?.runningCount || 0;
        const totalCount = stats?.totalCount || runningCount;
        const derivedState = runningCount > 0 ? "running" : "completed";
        if (idx >= 0) {
          next[idx] = {
            ...next[idx],
            meta: {
              ...(next[idx].meta || {}),
              state: derivedState,
              runningCount,
              totalCount,
            },
          };
          progressRef.current = next;
          return next;
        }
        // fall back to appending if no existing running entry
        const appended = [
          ...prev,
          {
            id: entryId,
            text,
            ...classified,
            meta: {
              ...(classified.meta || {}),
              runningCount,
              totalCount,
              state: derivedState,
            },
          },
        ];
        progressRef.current = appended;
        return appended;
      });
      setShowProgress(true);
      return;
    }

    setProgress((prev) => {
      const updated = [
        ...prev,
        { id: entryId, text, ...classified },
      ];
      progressRef.current = updated;
      return updated;
    });

    setShowProgress(true);
  };

  const handleProgressClear = () => {
    setShowProgress(true);
    setProgressExpanded(false);
    setAnalysisComplete(true);
    setIsAnalyzing(false);
    planModeRef.current = false;
    planSequenceRef.current = 0;

    const finishedRunId = currentRunIdRef.current;

    setConversationHistory((prev) =>
      prev.map((m) => {
        if (m.type === "assistant" && m.pending && m.runId === finishedRunId) {
          return {
            ...m,
            logsSnapshot:
              Array.isArray(m.logsSnapshot) && m.logsSnapshot.length
                ? m.logsSnapshot
                : [...(progressRef.current || [])],
            activeAgentsSnapshot:
              Array.isArray(m.activeAgentsSnapshot) &&
              m.activeAgentsSnapshot.length
                ? m.activeAgentsSnapshot
                : [...(activeAgentsRef.current || [])],
            toolEventsSnapshot:
              Array.isArray(m.toolEventsSnapshot) &&
              m.toolEventsSnapshot.length
                ? m.toolEventsSnapshot
                : [...(toolEventsByRunId[finishedRunId] || [])],
          };
        }
        return m;
      })
    );

    setActiveAgents([]);
    if (finishedRunId) {
      setRunCompletionMap((prev) => ({ ...prev, [finishedRunId]: true }));
      setSelectedRunId(finishedRunId);
      pendingDockRunIdRef.current = finishedRunId;
      setHasNewAgentReports(true);
      fetchVisualizationsForRun(finishedRunId);
    }
    setProgress([]);
  };

  const handleResetMemory = async () => {
    if (!datasetId) return;

    const sessionId = getOrCreateSessionId(datasetId);
    setIsResettingMem(true);
    try {
      const url =
        `${API_BASE}/reset_memory` +
        `?dataset_id=${encodeURIComponent(datasetId)}` +
        `&session_id=${encodeURIComponent(sessionId)}`;

      const res = await fetch(url, { method: "POST" });
      const json = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);

      setConversationHistory((prev) => [
        ...prev,
        {
          type: "system",
          message: "Memory for this session has been cleared.",
          timestamp: new Date(),
        },
      ]);
    } catch (err) {
      setConversationHistory((prev) => [
        ...prev,
        {
          type: "system",
          message: `Failed to reset memory: ${err.message || String(err)}`,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsResettingMem(false);
    }
  };

  const handleStatusUpdate = (status) => {
    let normalizedStatus;
    if (typeof status === "string") normalizedStatus = status;
    else {
      try {
        normalizedStatus = JSON.stringify(status);
      } catch {
        normalizedStatus = String(status ?? "");
      }
    }

    setCurrentStatus(normalizedStatus);
    setShowStatus(true);

    // keep agent highlighting behavior intact
    const detected = detectAgentFromStatus(normalizedStatus);
    if (detected) {
      if (normalizedStatus.toLowerCase().includes("running_now_")) {
        setActiveAgents([detected]);
      } else {
        setActiveAgents((prev) =>
          prev.includes(detected) ? prev : [...prev, detected]
        );
      }
    }
  };

  const handleStatusClear = () => {
    setShowStatus(false);
    setCurrentStatus("");
    setActiveAgents([]);
    setIsAnalyzing(false);
  };

  const handleReset = () => {
    setConversationHistory([]);
    setShowRightDock(false);
    setActiveAgents([]);
    setProgressExpanded(false);
    setAnalysisComplete(false);
    setIsAnalyzing(false);
    setProgress([]);
    setShowProgress(false);
    setProgressExpanded(false);
    setAnalysisComplete(false);
    setArtifactsByRunId({});
    setToolEventsByRunId({});
    setSelectedRunId(null);
    setHasNewAgentReports(false);
    setRunCompletionMap({});
    setVisualizationsByRunId({});
    planModeRef.current = false;
    planSequenceRef.current = 0;
    setCompletedAgents([]);
    resetTaskTracking();
  };

  const hasConversation = conversationHistory.length > 0;

  const currentArtifact =
    selectedRunId && artifactsByRunId[selectedRunId]
      ? artifactsByRunId[selectedRunId]
      : null;
  const selectedRunFinalReady = selectedRunId
    ? runCompletionMap.hasOwnProperty(selectedRunId)
      ? Boolean(runCompletionMap[selectedRunId])
      : true
    : false;

  const dockOffset = showRightDock
    ? RIGHT_DOCK_WIDTH + RIGHT_DOCK_TRIGGER_WIDTH
    : RIGHT_DOCK_TRIGGER_WIDTH;
  const mainContentStyle = {
    ...styles.mainContent,
    marginRight: `${dockOffset}px`,
  };

  useEffect(() => {
    if (
      selectedRunFinalReady &&
      selectedRunId &&
      !visualizationsByRunId[selectedRunId]
    ) {
      fetchVisualizationsForRun(selectedRunId);
    }
  }, [
    fetchVisualizationsForRun,
    selectedRunFinalReady,
    selectedRunId,
    visualizationsByRunId,
  ]);

  useEffect(() => {
    const runId = pendingDockRunIdRef.current;
    if (!runId) return;
    const state = visualizationsByRunId[runId];
    if (state?.status === "ready") {
      setShowRightDock(true);
      setHasNewAgentReports(false);
      pendingDockRunIdRef.current = null;
    }
  }, [visualizationsByRunId]);

  const latestRunIdForPeek =
    selectedRunId || runOptions[runOptions.length - 1]?.runId || null;
  const peekAgentCount = (() => {
    if (activeAgents.length) return activeAgents.length;
    if (latestRunIdForPeek && toolEventsByRunId[latestRunIdForPeek]) {
      const uniq = new Set(
        toolEventsByRunId[latestRunIdForPeek].map(
          (evt) => evt.agentName || evt.agentSlug || evt.toolSlug
        )
      );
      return uniq.size || 0;
    }
    return 0;
  })();
  const latestViz = latestRunIdForPeek
    ? visualizationsByRunId[latestRunIdForPeek]
    : null;
  let dockPeekLabel = "";
  if (hasNewAgentReports) {
    dockPeekLabel =
      peekAgentCount > 0
        ? `${peekAgentCount} agent update${peekAgentCount === 1 ? "" : "s"}`
        : "Agent updates";
  } else if (activeAgents.length) {
    dockPeekLabel = `${peekAgentCount} agent${
      peekAgentCount === 1 ? "" : "s"
    } running`;
  } else if (latestViz?.status === "ready") {
    dockPeekLabel = "Insights ready";
  }

  return (
    <Box sx={styles.container}>
      <div
        style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100 }}
      >
        <HeaderBar email={email} API_BASE={API_BASE} />
      </div>

      <Sidebar
        onReset={handleReset}
        onResetMemory={handleResetMemory}
        isResettingMem={isResettingMem}
        activeAgents={activeAgents}
        isAnalyzing={isAnalyzing}
      />

      <div style={mainContentStyle}>
        {!hasConversation ? (
          <div style={styles.centerArea}>
            <div style={styles.logoContainer}>
              <div style={styles.logo}>
                <img src={kai} alt="Logo" style={styles.logo} />
              </div>
            </div>
            <h1 style={styles.welcomeTitle}>What's on your agenda today?</h1>
            <div style={styles.inputContainer}>
              {(isAnalyzing ||
              (showProgress && progress.length > 0) ||
              analysisComplete) && (
              <AgentAnalyzingPanel
                activeAgents={activeAgents}
                isAnalyzing={isAnalyzing}
                progress={progress}
                analysisComplete={analysisComplete}
                progressExpanded={progressExpanded}
                onToggleExpand={() => setProgressExpanded(!progressExpanded)}
                progressPanelRef={progressPanelRef}
                completedAgentsCount={completedAgents.length}
                isDockOpen={showRightDock}
              />
            )}

              <ControlledAskBox
                ref={askBoxHeroRef}
                apiBase={API_BASE}
                datasetId={datasetId}
                onAsked={handleAsked}
                onAnswered={handleAnswered}
                onProgress={handleProgressAppend}
                onProgressClear={handleProgressClear}
                onStatus={handleStatusUpdate}
                onArtifacts={handleArtifacts} // safe if AskBox ignores
                onToolEvent={handleToolEvent}
                disabled={isAnalyzing}
                email={email}
              />
              
              {/* Suggestions */}
              <div style={styles.suggestionsWrap}>
                {[
                  "Analyze my MRO spend and compare it with Kearney's benchmarks",
                  // "Evaluate the degree of supplier fragmentation across my categories",
                  "Identify plants or regions with outlier pricing and suggest local suppliers to correct it",
                  "How have my category prices evolved vs trends in the market prices of the underlying commodities?",
                ].map((text, i) => (
                  <div
                    key={i}
                    style={{
                      ...styles.suggestionItem,
                      ...(i !== 2
                        ? { borderBottom: "0.5px solid #555770" }
                        : {}),
                    }}
                    onClick={() => askBoxHeroRef.current?.prefill?.(text)}
                  >
                    {text}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div style={styles.chatContainer}>
            <MessageList
              conversationHistory={conversationHistory}
              isAnalyzing={isAnalyzing}
              progress={progress}
              analysisComplete={analysisComplete}
              progressExpanded={progressExpanded}
              setProgressExpanded={setProgressExpanded}
              progressPanelRef={progressPanelRef}
              avatarSrc={avatarSrc}
              // artifacts per message
              artifactsByRunId={artifactsByRunId}
              onOpenArtifact={(runId) =>
                openRunInDock(runId, { userInitiated: true })
              }
              activeAgents={activeAgents}
              isDockOpen={showRightDock}
            />

            {/* ask dock */}
            <div style={styles.askDock}>
              <ControlledAskBox
                apiBase={API_BASE}
                datasetId={datasetId}
                onAsked={handleAsked}
                onAnswered={handleAnswered}
                onProgress={handleProgressAppend}
                onProgressClear={handleProgressClear}
                onStatus={handleStatusUpdate}
                onArtifacts={handleArtifacts} // safe if AskBox ignores
                onToolEvent={handleToolEvent}
                disabled={isAnalyzing}
                email={email}
              />
            </div>
          </div>
        )}
      </div>

      <AgentReportsRibbon
        isOpen={showRightDock}
        hasHighlight={hasNewAgentReports}
        peekLabel={dockPeekLabel}
        onToggle={handleRibbonToggle}
      />

      {showRightDock && (
        <RightDock
          apiBase={API_BASE}
          artifactImageUrl={currentArtifact?.artifact_image_url || null}
          artifactPlan={currentArtifact?.artifact_plan || null}
          toolEvents={
            selectedRunId
              ? toolEventsByRunId[selectedRunId] || []
              : []
          }
          runOptions={runOptions}
          selectedRunId={selectedRunId}
          onSelectRun={handleSelectRunFromDock}
          onClose={() => {
            setShowRightDock(false);
            setSelectedRunId(null);
            setHasNewAgentReports(false);
          }}
          email={email}
          finalAnswerReady={selectedRunFinalReady}
          visualizationsByRunId={visualizationsByRunId}
          onRequestVisualizations={fetchVisualizationsForRun}
        />
      )}
    </Box>
  );
}
